import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase } from '../lib/supabase';
import AppHeader from '../components/AppHeader';

interface UserStats {
  projectsCount: number;
  creditsRemaining: number;
  customFontsCount: number;
}

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, dir } = useLanguage();
  const [stats, setStats] = useState<UserStats>({ projectsCount: 0, creditsRemaining: 0, customFontsCount: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/landing');
      return;
    }

    const fetchStats = async () => {
      try {
        // Get projects count
        const { count: projectsCount } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        // Get credits from new FIFO system
        const { data: creditsValue } = await supabase.rpc('get_user_available_credits', { 
          p_user_id: user.id 
        });
        
        const credits = Number(creditsValue) || 0;

        // Get custom fonts count
        const { count: fontsCount } = await supabase
          .from('custom_fonts')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        setStats({
          projectsCount: projectsCount || 0,
          creditsRemaining: credits,
          customFontsCount: fontsCount || 0,
        });
      } catch (err) {
        console.error('Error fetching stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  return (
    <div className="min-h-screen bg-black flex flex-col" dir={dir}>
      <AppHeader />

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 w-full">
        {/* Welcome Section */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-3 sm:mb-4">
            {t('pages.home.welcome')}
          </h1>
          <p className="text-base sm:text-xl text-gray-400">{t('pages.home.subtitle')}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-8 sm:mb-12">
          {/* Projects Count */}
          <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/40 border-2 border-blue-500/50 rounded-xl p-3 sm:p-6 text-center">
            <div className="text-blue-400 text-xs sm:text-sm font-semibold mb-1 sm:mb-2">{t('pages.home.projects')}</div>
            <div className="text-2xl sm:text-5xl font-bold text-white mb-0.5 sm:mb-1">{loading ? '...' : stats.projectsCount}</div>
            <div className="text-blue-300 text-xs sm:text-sm">{t('pages.home.project')}</div>
          </div>

          {/* Credits */}
          <div className="bg-gradient-to-br from-yellow-900/40 to-yellow-800/40 border-2 border-yellow-500/50 rounded-xl p-3 sm:p-6 text-center">
            <div className="text-yellow-400 text-xs sm:text-sm font-semibold mb-1 sm:mb-2">{t('pages.home.credits')}</div>
            <div className="text-2xl sm:text-5xl font-bold text-white mb-0.5 sm:mb-1">{loading ? '...' : stats.creditsRemaining}</div>
            <div className="text-yellow-300 text-xs sm:text-sm">{t('pages.home.remaining')}</div>
          </div>

          {/* Custom Fonts */}
          <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/40 border-2 border-purple-500/50 rounded-xl p-3 sm:p-6 text-center">
            <div className="text-purple-400 text-xs sm:text-sm font-semibold mb-1 sm:mb-2">{t('pages.home.fonts')}</div>
            <div className="text-2xl sm:text-5xl font-bold text-white mb-0.5 sm:mb-1">{loading ? '...' : stats.customFontsCount}</div>
            <div className="text-purple-300 text-xs sm:text-sm">{t('pages.home.font')}</div>
          </div>
        </div>

        {/* Quick Actions Title */}
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">{t('pages.home.chooseWhat')}</h2>
          <p className="text-sm sm:text-base text-gray-400">{t('pages.home.startDescription')}</p>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-12">
          {/* Projects Option */}
          <button
            onClick={() => navigate('/dashboard')}
            className="group bg-gradient-to-br from-zinc-900 to-zinc-800 hover:from-blue-900/30 hover:to-blue-800/30 border-2 border-zinc-700 hover:border-blue-500 rounded-xl sm:rounded-2xl p-4 sm:p-8 transition-all duration-300 transform hover:scale-105"
          >
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 bg-blue-500/20 rounded-xl flex items-center justify-center group-hover:bg-blue-500/30 transition-all">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-xl font-bold text-white mb-1 sm:mb-2">{t('pages.home.projectsOption.title')}</h3>
              <p className="text-gray-400 text-xs sm:text-sm">
                {t('pages.home.projectsOption.desc')}
              </p>
            </div>
          </button>

          {/* Runner Option */}
          <button
            onClick={() => navigate('/runner')}
            className="group bg-gradient-to-br from-zinc-900 to-zinc-800 hover:from-green-900/30 hover:to-green-800/30 border-2 border-zinc-700 hover:border-green-500 rounded-xl sm:rounded-2xl p-4 sm:p-8 transition-all duration-300 transform hover:scale-105"
          >
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 bg-green-500/20 rounded-xl flex items-center justify-center group-hover:bg-green-500/30 transition-all">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-xl font-bold text-white mb-1 sm:mb-2">{t('pages.home.runnerOption.title')}</h3>
              <p className="text-gray-400 text-xs sm:text-sm">
                {t('pages.home.runnerOption.desc')}
              </p>
            </div>
          </button>

          {/* Credits Option */}
          <button
            onClick={() => navigate('/credits')}
            className="group bg-gradient-to-br from-zinc-900 to-zinc-800 hover:from-yellow-900/30 hover:to-yellow-800/30 border-2 border-zinc-700 hover:border-yellow-500 rounded-xl sm:rounded-2xl p-4 sm:p-8 transition-all duration-300 transform hover:scale-105"
          >
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 bg-yellow-500/20 rounded-xl flex items-center justify-center group-hover:bg-yellow-500/30 transition-all">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-xl font-bold text-white mb-1 sm:mb-2">{t('pages.home.creditsOption.title')}</h3>
              <p className="text-gray-400 text-xs sm:text-sm">
                {t('pages.home.creditsOption.desc')}
              </p>
            </div>
          </button>

          {/* Custom Fonts Option */}
          <button
            onClick={() => navigate('/custom-fonts')}
            className="group bg-gradient-to-br from-zinc-900 to-zinc-800 hover:from-purple-900/30 hover:to-purple-800/30 border-2 border-zinc-700 hover:border-purple-500 rounded-xl sm:rounded-2xl p-4 sm:p-8 transition-all duration-300 transform hover:scale-105"
          >
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 bg-purple-500/20 rounded-xl flex items-center justify-center group-hover:bg-purple-500/30 transition-all">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-xl font-bold text-white mb-1 sm:mb-2">{t('pages.home.fontsOption.title')}</h3>
              <p className="text-gray-400 text-xs sm:text-sm">
                {t('pages.home.fontsOption.desc')}
              </p>
            </div>
          </button>

          {/* API Tokens Option */}
          <button
            onClick={() => navigate('/api-tokens')}
            className="group bg-gradient-to-br from-zinc-900 to-zinc-800 hover:from-cyan-900/30 hover:to-cyan-800/30 border-2 border-zinc-700 hover:border-cyan-500 rounded-xl sm:rounded-2xl p-4 sm:p-8 transition-all duration-300 transform hover:scale-105"
          >
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 bg-cyan-500/20 rounded-xl flex items-center justify-center group-hover:bg-cyan-500/30 transition-all">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-xl font-bold text-white mb-1 sm:mb-2">{t('pages.home.apiTokensOption.title')}</h3>
              <p className="text-gray-400 text-xs sm:text-sm">
                {t('pages.home.apiTokensOption.desc')}
              </p>
            </div>
          </button>

          {/* Account Option */}
          <button
            onClick={() => navigate('/account')}
            className="group bg-gradient-to-br from-zinc-900 to-zinc-800 hover:from-orange-900/30 hover:to-orange-800/30 border-2 border-zinc-700 hover:border-orange-500 rounded-xl sm:rounded-2xl p-4 sm:p-8 transition-all duration-300 transform hover:scale-105"
          >
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 bg-orange-500/20 rounded-xl flex items-center justify-center group-hover:bg-orange-500/30 transition-all">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-xl font-bold text-white mb-1 sm:mb-2">{t('pages.home.accountOption.title')}</h3>
              <p className="text-gray-400 text-xs sm:text-sm">
                {t('pages.home.accountOption.desc')}
              </p>
            </div>
          </button>

        </div>

      </main>

      {/* Footer */}
      <footer className="bg-zinc-900 border-t border-zinc-800 py-6 sm:py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-6">
            {/* Powered By */}
            <div className="text-center md:text-right">
              <div className="text-gray-400 text-sm mb-2">{t('pages.home.footer.poweredBy')}</div>
              <a 
                href="https://www.audome.io/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 font-semibold text-lg transition-colors"
              >
                Audome Technology
              </a>
            </div>

            {/* Contact */}
            <div className="text-center">
              <div className="text-gray-400 text-sm mb-2">{t('pages.home.footer.contact')}</div>
              <a 
                href="mailto:ahmed@audome.io" 
                className="text-gray-300 hover:text-white transition-colors"
              >
                ahmed@audome.io
              </a>
            </div>

            {/* API Documentation */}
            <div className="text-center">
              <div className="text-gray-400 text-sm mb-2">{t('pages.home.footer.developers')}</div>
              <button
                onClick={() => navigate('/api-docs')}
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                {t('pages.home.footer.apiDocs')}
              </button>
            </div>

            {/* Legal */}
            <div className="text-center">
              <div className="text-gray-400 text-sm mb-2">{t('pages.home.footer.legal')}</div>
              <div className="flex flex-col gap-2 items-center">
                <button
                  onClick={() => navigate('/terms')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {t('pages.home.footer.terms')}
                </button>
                <button
                  onClick={() => navigate('/privacy')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {t('pages.home.footer.privacy')}
                </button>
              </div>
            </div>

            {/* Social */}
            <div className="text-center md:text-left">
              <div className="text-gray-400 text-sm mb-2">{t('pages.home.footer.follow')}</div>
              <a 
                href="https://www.instagram.com/audome.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-pink-400 hover:text-pink-300 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Instagram
              </a>
            </div>
          </div>

          {/* Copyright */}
          <div className="text-center text-gray-500 text-sm pt-6 border-t border-zinc-800">
            {t('pages.home.footer.copyright')}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;

